import { Footer, FooterLite, Header, Iframe } from '@aspendental/shared-components-web';
import { NavigationLink } from '@aspendental/shared-components-web/lib/graphql/__generated/sdk';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Head from 'next/head';
import router from 'next/router';
import { ReactNode, useEffect, useState } from 'react';
import ScheduleLayoutNew from '../ScheduleLayout/ScheduleLayout';
import ScheduleMobileHeader from '@src/components/ScheduleMobileHeader';
import { HydraState } from '@src/constants';
import { AdFooter, App, Maybe, SuccessCard } from '@src/lib/__generated/contentful/sdk';
import { useAppStore } from '@src/store';
import makeRequestHeaders from '@src/utils/make-request-headers';
interface LayoutPropsInterface {
  children: ReactNode;
  appData: Maybe<App>;
  footerData: Maybe<AdFooter>;
  isFooterLite?: boolean;
  preview: boolean;
  withSimpleLayout?: boolean;
  logoHref: string;
  smartyKey: string;
  successCards?: SuccessCard[];
}
export const Layout = ({
  appData,
  footerData,
  isFooterLite = true,
  withSimpleLayout = false,
  logoHref,
  smartyKey,
  successCards,
  children
}: LayoutPropsInterface) => {
  const [isClient, setIsClient] = useState(false);
  const {
    setSuccessCards,
    languageToggleEnabled,
    setSessionId,
    setTransformMarketingSourceEnabled,
    setOneTrust
  } = useAppStore(store => store);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    setIsClient(true);
    if (successCards?.length) {
      setSuccessCards(successCards);
    }
  }, [successCards, setSuccessCards]);
  useEffect(() => {
    const scopedBaseline = document.getElementById('mui-scoped-baseline');
    if (scopedBaseline?.style) {
      scopedBaseline.style.overflowX = 'unset';
    }
  }, []);
  useEffect(() => {
    const sessionId = getSessionId();
    const hydraPilotLocations = appData?.featureFlags?.hydraPilotLocations ?? [];
    const marketingSourceTransformEnabled = appData?.featureFlags?.enableCcMarketingSourceTransformation ?? false;
    setOneTrust(appData?.featureFlags?.oneTrust ?? false);
    setTransformMarketingSourceEnabled(marketingSourceTransformEnabled);
    setSessionId(sessionId);
    fetch(process.env.APP_PATH + '/api/clear-choice/hydra-status-log/', {
      headers: makeRequestHeaders({
        sessionId
      }),
      method: 'POST',
      body: JSON.stringify({
        data: {
          state: HydraState.ON,
          startingFacility: localStorage.getItem('facilityCode')?.replace(/"/g, '') || 'N/A',
          fullTraffic: hydraPilotLocations?.some(location => location === 'Full Traffic|ALL') || false
        }
      })
    });
  }, [appData]);
  if (!isClient) {
    return null;
  }
  const determinePhoneNumber = (phoneNumberNavItem: NavigationLink | undefined, phoneNumber: Maybe<NavigationLink> | undefined): string => {
    if (phoneNumberNavItem && phoneNumberNavItem.linkText !== undefined) {
      return phoneNumberNavItem.linkText || '888-486-3443';
    } else if (phoneNumber && phoneNumber.linkText !== undefined) {
      return phoneNumber.linkText || '888-486-3443';
    }
    return '888-486-3443';
  };
  const warningBanner = appData?.warningBanner;

  // single-tenant supported app for this Layout
  // so it is safe to assume that the header will always be fixed and the header nav menu will always be shown for now
  // if this changes in the future, we can add these to be driven by contentful
  const withFixedHeader = true;
  const showHeaderNavMenu = true;
  return <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }} data-sentry-element="Box" data-sentry-component="Layout" data-sentry-source-file="layout.tsx">
			<Head data-sentry-element="Head" data-sentry-source-file="layout.tsx">
				{router.pathname === '/2' && <meta name="robots" content="noindex" />}
				<title>Schedule Your Free Dental Implant Consultation In 3 Easy Steps | ClearChoice</title>
			</Head>
			{appData && <Box sx={{
      ".MuiBox-root[data-test-id='desktop-header-nav-menu']": {
        display: 'none'
      },
      ".MuiBox-root[data-test-id='section_header']": {
        minHeight: '4rem'
      }
    }}>
					{isMobile ? <ScheduleMobileHeader logoHref={logoHref} appData={appData} languageToggleEnabled={languageToggleEnabled} determinePhoneNumber={determinePhoneNumber} withFixedHeader={withFixedHeader} /> : <Header appData={appData} withMenuCTA={false} hideLocation={true} hidePrimaryActionButton={true} logoHref={logoHref} phoneNumber={appData?.header?.phoneNumber} showHeaderNavMenu={showHeaderNavMenu} withFixedHeader={withFixedHeader} useHeaderLocationCards={false} showLanguageToggle={languageToggleEnabled} />}
				</Box>}
			{router.pathname === '/404' ? children : <ScheduleLayoutNew smartyKey={smartyKey} baseUrl={logoHref} enablePtcSlotFilter={appData?.featureFlags?.enablePtcSlotFilter ?? false} />}
			{router.pathname === '/2' && warningBanner?.rawHtml ? <Iframe title={warningBanner?.title ?? ''} url={warningBanner?.url ?? ''} rawHtml={warningBanner?.rawHtml ?? ''} /> : null}
			{footerData && isFooterLite ? <FooterLite footerData={footerData} /> : <Footer footerData={footerData} withScheduling={withSimpleLayout} isLightFooter={!!footerData?.isLightFooter} />}
		</Box>;
};